import { supabase } from "@/integrations/supabase/client";
import type {
  MatchNotification,
  NotificationPreferencesRecord,
  NotificationPreferencesUpdate,
  NotificationPushSubscriptionRecord,
  NotificationPushSubscriptionUpsert,
} from "@/types/services/api-contracts";

import { requireAuthenticatedUser } from "./_core/auth";

export const notificationService = {
  async listMine(options?: {
    limit?: number;
    unreadOnly?: boolean;
  }): Promise<MatchNotification[]> {
    const user = await requireAuthenticatedUser();
    let query = supabase
      .from("notifications")
      .select("*")
      .eq("user_id", user.id)
      .order("created_at", { ascending: false });

    if (options?.unreadOnly) {
      query = query.eq("read", false);
    }

    if (typeof options?.limit === "number") {
      query = query.limit(options.limit);
    }

    const { data, error } = await query;

    if (error) throw error;
    return (data ?? []) as MatchNotification[];
  },

  async countUnread(): Promise<number> {
    const user = await requireAuthenticatedUser();
    const { count, error } = await supabase
      .from("notifications")
      .select("id", { head: true, count: "exact" })
      .eq("user_id", user.id)
      .eq("read", false);

    if (error) throw error;
    return count ?? 0;
  },

  async markRead(notificationId: string): Promise<MatchNotification> {
    const user = await requireAuthenticatedUser();
    const { data, error } = await supabase
      .from("notifications")
      .update({ read: true })
      .eq("id", notificationId)
      .eq("user_id", user.id)
      .select("*")
      .single();

    if (error) throw error;
    return data as MatchNotification;
  },

  async markAllRead(): Promise<{ success: boolean }> {
    const user = await requireAuthenticatedUser();
    const { error } = await supabase
      .from("notifications")
      .update({ read: true })
      .eq("user_id", user.id)
      .eq("read", false);

    if (error) throw error;
    return { success: true };
  },

  async getPreferences(): Promise<NotificationPreferencesRecord> {
    const user = await requireAuthenticatedUser();
    const { data, error } = await supabase
      .from("notification_preferences")
      .select("*")
      .eq("user_id", user.id)
      .maybeSingle();

    if (error) throw error;
    if (data) return data as NotificationPreferencesRecord;

    const now = new Date().toISOString();
    const { data: inserted, error: insertError } = await supabase
      .from("notification_preferences")
      .upsert(
        {
          user_id: user.id,
          booking_push_enabled: true,
          payment_push_enabled: true,
          arena_push_enabled: true,
          system_push_enabled: true,
          reminder_push_enabled: true,
          campaign_push_enabled: false,
          quiet_hours_enabled: false,
          quiet_hours_start_hour: null,
          quiet_hours_end_hour: null,
          timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || "America/Sao_Paulo",
          updated_at: now,
        },
        { onConflict: "user_id" },
      )
      .select("*")
      .single();

    if (insertError) throw insertError;
    return inserted as NotificationPreferencesRecord;
  },

  async updatePreferences(data: NotificationPreferencesUpdate): Promise<NotificationPreferencesRecord> {
    const user = await requireAuthenticatedUser();
    const { data: row, error } = await supabase
      .from("notification_preferences")
      .upsert(
        {
          user_id: user.id,
          ...data,
          updated_at: new Date().toISOString(),
        },
        { onConflict: "user_id" },
      )
      .select("*")
      .single();

    if (error) throw error;
    return row as NotificationPreferencesRecord;
  },

  async listPushSubscriptions(): Promise<NotificationPushSubscriptionRecord[]> {
    const user = await requireAuthenticatedUser();
    const { data, error } = await supabase
      .from("notification_push_subscriptions")
      .select("*")
      .eq("user_id", user.id)
      .order("updated_at", { ascending: false });

    if (error) throw error;
    return (data ?? []) as NotificationPushSubscriptionRecord[];
  },

  async upsertPushSubscription(data: NotificationPushSubscriptionUpsert): Promise<NotificationPushSubscriptionRecord> {
    const user = await requireAuthenticatedUser();
    const now = new Date().toISOString();
    const { data: row, error } = await supabase
      .from("notification_push_subscriptions")
      .upsert(
        {
          user_id: user.id,
          endpoint: data.endpoint,
          p256dh_key: data.p256dh_key,
          auth_key: data.auth_key,
          platform: data.platform ?? "web",
          device_label: data.device_label ?? null,
          browser_name: data.browser_name ?? null,
          user_agent: data.user_agent ?? null,
          active: true,
          last_seen_at: now,
          updated_at: now,
        },
        { onConflict: "user_id,endpoint" },
      )
      .select("*")
      .single();

    if (error) throw error;
    return row as NotificationPushSubscriptionRecord;
  },

  async deactivatePushSubscription(endpoint: string): Promise<NotificationPushSubscriptionRecord[]> {
    const user = await requireAuthenticatedUser();
    const { data, error } = await supabase
      .from("notification_push_subscriptions")
      .update({
        active: false,
        updated_at: new Date().toISOString(),
      })
      .eq("user_id", user.id)
      .eq("endpoint", endpoint)
      .select("*");

    if (error) throw error;
    return (data ?? []) as NotificationPushSubscriptionRecord[];
  },
};
